import React, { useState, useEffect } from 'react';

const IncomeTaxCalculator = () => {
  const [income, setIncome] = useState('');
  const [regime, setRegime] = useState('new'); // 'old' or 'new'
  const [age, setAge] = useState('below60');
  const [businessType, setBusinessType] = useState('individual');
  
  // Deductions for old regime
  const [deduction80C, setDeduction80C] = useState('');
  const [deduction80D, setDeduction80D] = useState('');
  const [deduction80E, setDeduction80E] = useState('');
  const [deduction80G, setDeduction80G] = useState('');
  const [hraExemption, setHraExemption] = useState('');
  const [homeLoanInterest, setHomeLoanInterest] = useState('');
  const [otherDeductions, setOtherDeductions] = useState('');
  
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (income && !isNaN(income) && parseFloat(income) > 0) {
      calculateTax();
    } else {
      setResults(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [income, regime, age, businessType, deduction80C, deduction80D, deduction80E, 
      deduction80G, hraExemption, homeLoanInterest, otherDeductions]);

  const calculateTax = () => {
    const grossIncome = parseFloat(income) || 0;
    let taxableIncome = grossIncome;
    let totalDeductions = 0;

    // Calculate deductions (only for old regime)
    if (regime === 'old') {
      const ded80C = Math.min(parseFloat(deduction80C) || 0, 150000);
      const ded80D = Math.min(parseFloat(deduction80D) || 0, age === 'senior' ? 50000 : 25000);
      const ded80E = parseFloat(deduction80E) || 0;
      const ded80G = parseFloat(deduction80G) || 0;
      const hra = parseFloat(hraExemption) || 0;
      const homeLoan = Math.min(parseFloat(homeLoanInterest) || 0, 200000);
      const other = parseFloat(otherDeductions) || 0;

      totalDeductions = ded80C + ded80D + ded80E + ded80G + hra + homeLoan + other;
      
      // Standard deduction
      totalDeductions += 50000;
    } else {
      // New regime: Only standard deduction
      totalDeductions = 50000;
    }

    taxableIncome = Math.max(0, grossIncome - totalDeductions);

    // Calculate tax based on regime and slabs
    let tax = 0;
    let slabDetails = [];

    if (regime === 'new') {
      // New Tax Regime (FY 2023-24)
      slabDetails = calculateNewRegimeTax(taxableIncome);
      tax = slabDetails.reduce((sum, slab) => sum + parseFloat(slab.tax || 0), 0);
    } else {
      // Old Tax Regime
      slabDetails = calculateOldRegimeTax(taxableIncome, age);
      tax = slabDetails.reduce((sum, slab) => sum + parseFloat(slab.tax || 0), 0);
    }

    // Add cess
    const cess = tax * 0.04;
    const totalTax = tax + cess;

    // Calculate effective tax rate
    const effectiveRate = grossIncome > 0 ? (totalTax / grossIncome) * 100 : 0;

    // Monthly breakdown
    const monthlyIncome = grossIncome / 12;
    const monthlyTax = totalTax / 12;
    const monthlyTakeHome = monthlyIncome - monthlyTax;

    setResults({
      grossIncome: grossIncome.toFixed(2),
      totalDeductions: totalDeductions.toFixed(2),
      taxableIncome: taxableIncome.toFixed(2),
      tax: tax.toFixed(2),
      cess: cess.toFixed(2),
      totalTax: totalTax.toFixed(2),
      netIncome: (grossIncome - totalTax).toFixed(2),
      effectiveRate: effectiveRate.toFixed(2),
      slabDetails,
      monthlyIncome: monthlyIncome.toFixed(2),
      monthlyTax: monthlyTax.toFixed(2),
      monthlyTakeHome: monthlyTakeHome.toFixed(2),
    });
  };

  const calculateNewRegimeTax = (taxableIncome) => {
    const slabs = [
      { limit: 300000, rate: 0, name: 'Up to ₹3L' },
      { limit: 600000, rate: 5, name: '₹3L - ₹6L' },
      { limit: 900000, rate: 10, name: '₹6L - ₹9L' },
      { limit: 1200000, rate: 15, name: '₹9L - ₹12L' },
      { limit: 1500000, rate: 20, name: '₹12L - ₹15L' },
      { limit: Infinity, rate: 30, name: 'Above ₹15L' },
    ];

    return calculateSlabTax(taxableIncome, slabs);
  };

  const calculateOldRegimeTax = (taxableIncome, age) => {
    let basicExemption = 250000;
    if (age === 'senior') basicExemption = 300000;
    if (age === 'supersenior') basicExemption = 500000;

    const slabs = [
      { limit: basicExemption, rate: 0, name: `Up to ₹${basicExemption / 100000}L` },
      { limit: 500000, rate: 5, name: `₹${basicExemption / 100000}L - ₹5L` },
      { limit: 1000000, rate: 20, name: '₹5L - ₹10L' },
      { limit: Infinity, rate: 30, name: 'Above ₹10L' },
    ];

    return calculateSlabTax(taxableIncome, slabs);
  };

  const calculateSlabTax = (taxableIncome, slabs) => {
    let remaining = taxableIncome;
    let previousLimit = 0;
    const details = [];

    for (const slab of slabs) {
      if (remaining <= 0) break;

      const slabAmount = Math.min(remaining, slab.limit - previousLimit);
      const slabTax = (slabAmount * slab.rate) / 100;

      if (slabAmount > 0) {
        details.push({
          name: slab.name,
          rate: slab.rate,
          amount: parseFloat(slabAmount.toFixed(2)),
          tax: parseFloat(slabTax.toFixed(2)),
        });
      }

      remaining -= slabAmount;
      previousLimit = slab.limit;
    }

    return details;
  };

  const copyToClipboard = () => {
    if (!results) return;
    
    const summary = `
💰 Income Tax Calculation Summary
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Tax Regime: ${regime === 'new' ? 'New' : 'Old'}
Gross Income: ₹${results.grossIncome}
Total Deductions: ₹${results.totalDeductions}
Taxable Income: ₹${results.taxableIncome}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Income Tax: ₹${results.tax}
Cess (4%): ₹${results.cess}
Total Tax: ₹${results.totalTax}
Net Income: ₹${results.netIncome}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Effective Tax Rate: ${results.effectiveRate}%
Monthly Take-Home: ₹${results.monthlyTakeHome}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Generated via Vyqda.com/tools
    `;
    
    navigator.clipboard.writeText(summary.trim());
    alert('✅ Tax summary copied to clipboard!');
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Input Section */}
      <div className="card">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <span className="mr-2">💰</span> Income Tax Calculator
        </h2>

        <div className="space-y-5">
          {/* Gross Annual Income */}
          <div>
            <label className="label">Gross Annual Income</label>
            <div className="relative">
              <span className="absolute left-4 top-3.5 text-gray-500 font-semibold">₹</span>
              <input
                type="number"
                value={income}
                onChange={(e) => setIncome(e.target.value)}
                placeholder="Enter annual income"
                className="input-field pl-8"
                min="0"
                step="1000"
              />
            </div>
          </div>

          {/* Tax Regime */}
          <div>
            <label className="label">Tax Regime (FY 2023-24)</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setRegime('new')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  regime === 'new'
                    ? 'border-primary-600 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="font-semibold">New Regime</div>
                <div className="text-xs text-gray-500">Lower rates, no deductions</div>
              </button>
              <button
                onClick={() => setRegime('old')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  regime === 'old'
                    ? 'border-primary-600 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="font-semibold">Old Regime</div>
                <div className="text-xs text-gray-500">Higher rates, with deductions</div>
              </button>
            </div>
          </div>

          {/* Age Category */}
          <div>
            <label className="label">Age Category</label>
            <select value={age} onChange={(e) => setAge(e.target.value)} className="input-field">
              <option value="below60">Below 60 years</option>
              <option value="senior">Senior Citizen (60-80 years)</option>
              <option value="supersenior">Super Senior (Above 80 years)</option>
            </select>
          </div>

          {/* Business Type */}
          <div>
            <label className="label">Taxpayer Type</label>
            <select
              value={businessType}
              onChange={(e) => setBusinessType(e.target.value)}
              className="input-field"
            >
              <option value="individual">Individual / Salaried</option>
              <option value="proprietor">Sole Proprietor</option>
              <option value="professional">Professional / Freelancer</option>
            </select>
          </div>

          {/* Deductions - Only for Old Regime */}
          {regime === 'old' && (
            <div className="space-y-4 border-t pt-4">
              <h3 className="font-semibold text-gray-800 flex items-center">
                <span className="mr-2">📝</span> Deductions & Exemptions
              </h3>

              <div>
                <label className="label">80C - Investments (Max ₹1.5L)</label>
                <input
                  type="number"
                  value={deduction80C}
                  onChange={(e) => setDeduction80C(e.target.value)}
                  placeholder="PPF, ELSS, LIC, etc."
                  className="input-field"
                  min="0"
                />
                <p className="text-xs text-gray-500 mt-1">
                  EPF, PPF, ELSS, LIC, NSC, Tax Saver FD
                </p>
              </div>

              <div>
                <label className="label">80D - Health Insurance</label>
                <input
                  type="number"
                  value={deduction80D}
                  onChange={(e) => setDeduction80D(e.target.value)}
                  placeholder="Medical insurance premium"
                  className="input-field"
                  min="0"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Max ₹{age === 'senior' ? '50,000' : '25,000'}
                </p>
              </div>

              <div>
                <label className="label">80E - Education Loan Interest</label>
                <input
                  type="number"
                  value={deduction80E}
                  onChange={(e) => setDeduction80E(e.target.value)}
                  placeholder="Interest paid on education loan"
                  className="input-field"
                  min="0"
                />
              </div>

              <div>
                <label className="label">80G - Donations</label>
                <input
                  type="number"
                  value={deduction80G}
                  onChange={(e) => setDeduction80G(e.target.value)}
                  placeholder="Donations to charitable trusts"
                  className="input-field"
                  min="0"
                />
              </div>

              <div>
                <label className="label">HRA Exemption</label>
                <input
                  type="number"
                  value={hraExemption}
                  onChange={(e) => setHraExemption(e.target.value)}
                  placeholder="House Rent Allowance exemption"
                  className="input-field"
                  min="0"
                />
              </div>

              <div>
                <label className="label">24(b) - Home Loan Interest (Max ₹2L)</label>
                <input
                  type="number"
                  value={homeLoanInterest}
                  onChange={(e) => setHomeLoanInterest(e.target.value)}
                  placeholder="Interest on home loan"
                  className="input-field"
                  min="0"
                />
              </div>

              <div>
                <label className="label">Other Deductions</label>
                <input
                  type="number"
                  value={otherDeductions}
                  onChange={(e) => setOtherDeductions(e.target.value)}
                  placeholder="Other eligible deductions"
                  className="input-field"
                  min="0"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Results Section */}
      <div className="space-y-6">
        {results ? (
          <>
            <div className="card">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">📊</span> Tax Calculation
              </h3>

              <div className="space-y-4">
                {/* Income Breakdown */}
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Gross Income</span>
                    <span className="font-semibold">₹{results.grossIncome}</span>
                  </div>
                  <div className="flex justify-between text-success-600">
                    <span>Less: Deductions</span>
                    <span className="font-semibold">-₹{results.totalDeductions}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2 text-lg">
                    <span className="font-semibold">Taxable Income</span>
                    <span className="font-bold text-primary-600">₹{results.taxableIncome}</span>
                  </div>
                </div>

                {/* Tax Slabs */}
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">Tax Slab Breakdown</h4>
                  <div className="space-y-2">
                    {results.slabDetails.map((slab, index) => (
                      <div key={index} className="bg-blue-50 rounded-lg p-3">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-sm font-medium text-gray-700">{slab.name}</span>
                          <span className="text-xs bg-blue-200 px-2 py-1 rounded">{slab.rate}%</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">₹{parseFloat(slab.amount).toFixed(2)}</span>
                          <span className="font-semibold text-primary-600">Tax: ₹{parseFloat(slab.tax).toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Total Tax */}
                <div className="result-card">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Income Tax</span>
                      <span className="font-semibold">₹{results.tax}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Health & Education Cess (4%)</span>
                      <span className="font-semibold">₹{results.cess}</span>
                    </div>
                    <div className="flex justify-between border-t pt-2 text-lg">
                      <span className="font-semibold">Total Tax Payable</span>
                      <span className="font-bold text-red-600">₹{results.totalTax}</span>
                    </div>
                  </div>
                </div>

                {/* Net Income */}
                <div className="result-card bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700 font-semibold">Net Income After Tax</span>
                    <span className="text-3xl font-bold text-success-600">₹{results.netIncome}</span>
                  </div>
                  <div className="mt-2 pt-2 border-t border-green-200 flex justify-between text-sm">
                    <span className="text-gray-600">Effective Tax Rate</span>
                    <span className="font-semibold">{results.effectiveRate}%</span>
                  </div>
                </div>

                {/* Monthly Breakdown */}
                <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                  <h4 className="font-semibold text-indigo-900 mb-3">Monthly Breakdown</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-indigo-700">Monthly Income</span>
                      <span className="font-semibold">₹{results.monthlyIncome}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-indigo-700">Monthly Tax</span>
                      <span className="font-semibold">₹{results.monthlyTax}</span>
                    </div>
                    <div className="flex justify-between border-t border-indigo-300 pt-2">
                      <span className="font-semibold text-indigo-900">Monthly Take-Home</span>
                      <span className="font-bold text-indigo-600">₹{results.monthlyTakeHome}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="card">
              <h4 className="font-semibold text-gray-700 mb-3">Quick Actions</h4>
              <div className="flex flex-wrap gap-3">
                <button onClick={copyToClipboard} className="btn-primary flex items-center">
                  <span className="mr-2">📋</span> Copy Summary
                </button>
                <button onClick={() => window.print()} className="btn-secondary flex items-center no-print">
                  <span className="mr-2">🖨️</span> Print
                </button>
              </div>
            </div>

            {/* Tax Saving Tips */}
            <div className="card bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200">
              <h4 className="font-semibold text-purple-900 mb-2 flex items-center">
                <span className="mr-2">💡</span> Tax Saving Tips
              </h4>
              <ul className="text-sm text-purple-800 space-y-1">
                <li>• {regime === 'new' ? 'Consider old regime if deductions exceed ₹2.5L' : 'Consider new regime if you have fewer deductions'}</li>
                <li>• Contribute to PPF/ELSS for 80C benefits (old regime)</li>
                <li>• Buy health insurance for 80D deduction</li>
                <li>• File ITR before July 31st to avoid penalty</li>
                <li>• Pay advance tax to avoid interest under 234B/234C</li>
                <li>• Keep Form 16 and investment proofs ready</li>
              </ul>
            </div>
          </>
        ) : (
          <div className="card text-center py-12">
            <div className="text-6xl mb-4">💰</div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Ready to Calculate Tax</h3>
            <p className="text-gray-500">Enter your income to see tax breakdown</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default IncomeTaxCalculator;

